/**
 * EquipmentAssign — step 4 (Assign). Pick the equipment the device feeds, then
 * map each mapped register onto one of the equipment's bindable ports.
 *
 * Reuses the exact matching + picker the CommissionWizard uses: `suggestTagForParam`
 * / `nameScore` (lifted into ./commit), `compatibleKinds`, and `TagSearchCombobox`.
 * The candidate "tags" here are the mapped register rows turned into pseudo
 * `TagOption`s (id = row.key), so a port binds to a register row. The actual
 * cloud write (create tags + `twinBindings.create`) happens once at Go-live via
 * `commitConnection`, so backing out of this step never leaves half-created state.
 */

import { useMemo, useState } from "react";
import { Boxes, Check, Search, Sparkles, X } from "lucide-react";
import type { DataKind, EntityDef, ParameterDef, PlantNode } from "@foundry/foundry-plant";
import type { TagOption } from "@/lib/api";
import { TagSearchCombobox, compatibleKinds } from "@/components/twin/TagSearchCombobox";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Skeleton } from "@/lib/ui/skeleton";
import { useToast } from "@/lib/ui/toast";
import { cn } from "@/lib/ui/cn";
import { entityClassLabel } from "@/lib/plant/kind-labels";
import { bindablePorts, pseudoTagsFromRows, suggestTagForParam } from "./commit";
import type { TagRow } from "@/components/iot/ThingTagRows";

const CLASS_BADGE: Record<EntityDef["class"], "info" | "default" | "success" | "warning"> = {
  STATION: "info",
  EQUIPMENT: "default",
  INSTRUMENT: "success",
  TRANSPORT: "warning",
};

export interface EquipmentAssignProps {
  entities: PlantNode[];
  entitiesLoading: boolean;
  entityId: string | null;
  onEntityId: (id: string) => void;
  rows: TagRow[];
  /** port name → row.key (the binding plan). */
  binds: Record<string, string>;
  onBinds: (next: Record<string, string>) => void;
  /** "pick" (equipment-first step 1): just choose the equipment + preview its
   *  planned tags. "bind" (default, Assign step): map its ports to the registers. */
  variant?: "pick" | "bind";
}

export function EquipmentAssign({
  entities,
  entitiesLoading,
  entityId,
  onEntityId,
  rows,
  binds,
  onBinds,
  variant = "bind",
}: EquipmentAssignProps) {
  const toast = useToast();
  const pseudoTags = useMemo(() => pseudoTagsFromRows(rows), [rows]);
  const tagById = useMemo(() => {
    const m = new Map<string, TagOption>();
    for (const t of pseudoTags) m.set(t.id, t);
    return m;
  }, [pseudoTags]);

  const selected = useMemo(
    () => entities.find((e) => e.id === entityId) ?? null,
    [entities, entityId],
  );
  const ports = useMemo(() => (selected ? bindablePorts(selected.entity) : []), [selected]);
  const chosenCount = ports.filter((p) => binds[p.name]).length;

  const setBind = (port: string, tagId: string | null) => {
    const next = { ...binds };
    if (tagId) next[port] = tagId;
    else delete next[port];
    onBinds(next);
  };

  const acceptAll = () => {
    const next = { ...binds };
    let added = 0;
    for (const port of ports) {
      if (next[port.name]) continue;
      const s = suggestTagForParam(port, pseudoTags);
      if (s) {
        next[port.name] = s.tag.id;
        added += 1;
      }
    }
    onBinds(next);
    toast.push(
      added ? "ok" : "info",
      added ? `Matched ${added} port${added === 1 ? "" : "s"}` : "No confident matches to accept",
    );
  };

  // Equipment-first step 1: choose the equipment and preview its planned tags.
  if (variant === "pick") {
    return (
      <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
        <EquipmentPicker
          entities={entities}
          loading={entitiesLoading}
          entityId={entityId}
          onEntityId={onEntityId}
        />
        <div className="flex min-w-0 flex-col gap-3">
          {!selected ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border bg-surface-elevated/40 p-8 text-center text-sm text-on-surface-muted">
              Pick the equipment this device will feed.
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-surface-elevated p-4">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-sm font-medium text-on-surface">{selected.instanceName}</span>
                <Badge variant={CLASS_BADGE[selected.entity.class]} className="text-[8px]">
                  {entityClassLabel(selected.entity.class)}
                </Badge>
              </div>
              <p className="mb-3 text-xs text-on-surface-muted">The device you connect feeds these ports.</p>
              <div className="flex flex-wrap gap-1.5">
                {ports.length === 0 ? (
                  <span className="text-xs text-on-surface-muted">
                    This equipment has no mapped ports to feed.
                  </span>
                ) : (
                  ports.map((p) => (
                    <Badge key={p.name} variant="secondary" className="font-mono text-[10px]">
                      {p.name.replace(/_/g, " ")}
                      {p.unit ? ` · ${p.unit}` : ""}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
      <EquipmentPicker
        entities={entities}
        loading={entitiesLoading}
        entityId={entityId}
        onEntityId={onEntityId}
      />

      <div className="flex min-w-0 flex-col gap-3">
        {!selected ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border bg-surface-elevated/40 p-8 text-center text-sm text-on-surface-muted">
            Pick the equipment whose ports this device feeds.
          </div>
        ) : ports.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-elevated/40 p-6 text-center text-sm text-on-surface-muted">
            {selected.instanceName} has no ports to bind. Go live anyway; bind later from Equipment.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-on-surface-muted">Bind each port to a tag. Suggestions match by name.</p>
              <Button variant="outline" size="sm" onClick={acceptAll} disabled={pseudoTags.length === 0}>
                <Sparkles className="mr-1.5 size-4" />
                Accept all
              </Button>
            </div>

            <div className="flex flex-col gap-2">
              {ports.map((port) => (
                <PortBindRow
                  key={port.name}
                  port={port}
                  tags={pseudoTags}
                  chosen={binds[port.name] ? (tagById.get(binds[port.name]) ?? null) : null}
                  onBind={(tagId) => setBind(port.name, tagId)}
                />
              ))}
            </div>

            <p className="text-xs text-on-surface-muted">
              {chosenCount} of {ports.length} ports bound · {pseudoTags.length} tags available.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function EquipmentPicker({
  entities,
  loading,
  entityId,
  onEntityId,
}: {
  entities: PlantNode[];
  loading: boolean;
  entityId: string | null;
  onEntityId: (id: string) => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const n = query.trim().toLowerCase();
    if (!n) return entities;
    return entities.filter(
      (e) =>
        e.instanceName.toLowerCase().includes(n) ||
        e.entity.templateId.toLowerCase().includes(n),
    );
  }, [entities, query]);

  return (
    <aside className="flex max-h-[60vh] flex-col gap-2 rounded-lg border border-border bg-surface-elevated p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-on-surface-muted">
        <Boxes className="size-4" />
        Equipment ({entities.length})
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-on-surface-muted" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search equipment…"
          aria-label="Search equipment"
          className="h-8 pl-8 text-xs"
        />
      </div>
      <div className="flex flex-col gap-0.5 overflow-y-auto pr-1">
        {loading ? (
          <div className="flex flex-col gap-1.5 px-2 py-2" aria-busy="true" aria-label="Loading equipment">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
            <Skeleton className="h-6 w-4/6" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="px-2 py-4 text-xs text-on-surface-muted">
            {entities.length === 0
              ? "No equipment in this plant yet. Commission equipment first."
              : "No equipment matches."}
          </p>
        ) : (
          filtered.map((e) => {
            const active = e.id === entityId;
            return (
              <button
                key={e.id}
                type="button"
                aria-pressed={active}
                onClick={() => onEntityId(e.id)}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                  active
                    ? "bg-brand-muted text-brand"
                    : "text-on-surface-muted hover:bg-surface-muted hover:text-on-surface",
                )}
              >
                <span className="min-w-0 flex-1 truncate">{e.instanceName}</span>
                <Badge variant={CLASS_BADGE[e.entity.class]} className="shrink-0 text-[8px]">
                  {entityClassLabel(e.entity.class)}
                </Badge>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}

function PortBindRow({
  port,
  tags,
  chosen,
  onBind,
}: {
  port: ParameterDef & { dataKind: DataKind };
  tags: TagOption[];
  chosen: TagOption | null;
  onBind: (tagId: string | null) => void;
}) {
  const allowed = compatibleKinds(port.dataKind);
  const compat = useMemo(
    () => tags.filter((t) => allowed.has(t.data_kind as DataKind) && t.id !== chosen?.id),
    [tags, allowed, chosen],
  );
  const incompat = useMemo(
    () => tags.filter((t) => !allowed.has(t.data_kind as DataKind)),
    [tags, allowed],
  );
  const suggestion = useMemo(
    () => (chosen ? null : suggestTagForParam(port, tags)),
    [chosen, port, tags],
  );

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-on-surface">{port.name.replace(/_/g, " ")}</span>
        <Badge variant="secondary" className="font-mono text-[9px]">
          {port.dataKind}
        </Badge>
        {port.unit && <span className="text-[11px] text-on-surface-muted">{port.unit}</span>}
        <div className="ml-auto">
          {chosen ? (
            <span className="flex items-center gap-1.5 rounded-md bg-success/10 px-2 py-1 text-xs text-success">
              <Check className="size-3.5" />
              <span className="font-mono">{chosen.name}</span>
              <button
                type="button"
                onClick={() => onBind(null)}
                className="text-success/70 hover:text-danger"
                aria-label={`Clear binding for ${port.name}`}
              >
                <X className="size-3.5" />
              </button>
            </span>
          ) : (
            <Badge variant="outline" className="text-[9px] uppercase tracking-wide text-on-surface-muted">
              unbound
            </Badge>
          )}
        </div>
      </div>

      {suggestion && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-info/40 bg-info/5 px-2 py-1.5 text-xs">
          <Sparkles className="size-3.5 text-info" />
          <span className="text-on-surface-muted">Suggested:</span>
          <span className="font-mono text-on-surface">{suggestion.tag.name}</span>
          <span className="text-[10px] text-on-surface-muted">{Math.round(suggestion.score * 100)}%</span>
          <Button size="sm" variant="ghost" className="ml-auto h-6" onClick={() => onBind(suggestion.tag.id)}>
            Accept
          </Button>
        </div>
      )}

      {tags.length > 0 && (
        <div className="mt-2">
          <TagSearchCombobox
            tags={compat}
            onSelect={(tagId) => onBind(tagId)}
            placeholder={chosen ? "Change tag…" : "Bind a tag: search by name…"}
            emptyLabel="No compatible tags"
            incompatibleTags={incompat}
            incompatibleReason={`needs ${port.dataKind}`}
          />
        </div>
      )}
    </div>
  );
}
